import classNames from 'classnames';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { MdSearch } from 'react-icons/md';
import { Empty, Select } from '@vyaguta/vyaguta-ui';

import useQuery from 'hooks/useQuery';
import useTitle from 'hooks/useTitle';

import { DEFAULT_PAGE } from 'constants/page';
import { YEAR_OPTIONS } from 'constants/options';
import { DEFAULT_PAGE_SIZE } from 'constants/appConstants';

import * as noticeService from 'services/notices';

import { getFormattedDate } from 'utils/date';
import { handleError } from 'utils/errorHandler';
import { getDefaultYearFilter } from 'utils/notice';

import NoticeBoardCard from './components/NoticeBoardCard';

import type { Notice } from 'interface/notices';
import type { QueryObject, RouterProps } from 'interface/queryParam';

type NoticeBoardQuery = QueryObject & {
  year?: string;
  q?: string;
};

type NoticeBoardResponse = {
  data: NoticeItem[];
  meta: {
    page: number;
    size: number;
    total: number;
  };
};

type NoticeItem = Notice & {
  id: number;
};

type YearOption = {
  value: number;
  label: string;
};

type YearSelectProps = {
  isSearchable?: boolean;
  autoResize?: boolean;
  options: YearOption[];
  value: YearOption | null;
  onChange: (option: YearOption | null) => void;
  placeholder?: string;
};

const YearSelect = Select as unknown as React.ComponentType<YearSelectProps>;

const toYearQueryValue = (year: number | string) => String(year);

type Props = Partial<RouterProps>;

const NoticeBoard: React.FC<Props> = (props) => {
  useTitle('Noticeboard');

  const currentYear = moment().year();
  const currentDate = getFormattedDate();

  const [query, setQuery] = useQuery<Props>(props);
  const typedQuery = query as NoticeBoardQuery;
  const hasAppliedYearFilter = typedQuery.year != null && Number(typedQuery.year) !== currentYear;
  const [isLoading, setIsLoading] = useState(false);
  const [isFilterChanged, setIsFilterChanged] = useState(false);
  const isFilterApplied = Boolean(typedQuery.q) || hasAppliedYearFilter;
  const [filters, setFilters] = useState<NoticeBoardQuery>({ ...typedQuery });
  const [noticeList, setNoticeList] = useState<NoticeItem[]>([]);
  const [noticeCount, setNoticeCount] = useState<number>();
  const [searchQuery, setSearchQuery] = useState(typedQuery.q ?? '');

  const applyFilter = (filter: NoticeBoardQuery) => {
    setQuery({ ...typedQuery, ...filter });
  };

  const handleYearChange = (option: YearOption | null) => {
    if (!option) {
      return;
    }

    setFilters((previousFilters) => ({ ...previousFilters, year: toYearQueryValue(option.value) }));
    setIsFilterChanged(true);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();

    const textValue = event.target.value.trimStart();

    setSearchQuery(textValue);
    setFilters((previousFilters) => ({ ...previousFilters, q: textValue }));
    setIsFilterChanged(true);
  };

  const handleApplyFilter = () => {
    if (!isFilterChanged) {
      return;
    }

    applyFilter(filters);
    setIsFilterChanged(false);
  };

  const handleReset = () => {
    if (!isFilterApplied) {
      return;
    }

    setFilters({});
    setSearchQuery('');
    applyFilter({ year: toYearQueryValue(YEAR_OPTIONS[1].value), q: '' });
  };

  useEffect(() => {
    if (!typedQuery.year) {
      setQuery(getDefaultYearFilter());

      return;
    }

    let isMounted = true;

    const loadNoticeBoard = async () => {
      try {
        setIsLoading(true);

        const appliedFilters = {
          page: DEFAULT_PAGE,
          size: DEFAULT_PAGE_SIZE,
          year: toYearQueryValue(typedQuery.year),
          q: typedQuery.q,
        };

        const { meta } = (await noticeService.fetchNoticeBoard(appliedFilters)) as NoticeBoardResponse;
        const { page, size, total } = meta;
        const totalPages = Math.ceil(total / size);

        if (!isMounted) {
          return;
        }

        setNoticeCount(total);

        const fetchAllPromise: Array<Promise<NoticeBoardResponse>> = [];

        for (let pageNumber = page; pageNumber <= totalPages; pageNumber += 1) {
          fetchAllPromise.push(
            noticeService.fetchNoticeBoard({
              page: pageNumber,
              size: DEFAULT_PAGE_SIZE,
              year: toYearQueryValue(typedQuery.year),
              q: typedQuery.q,
            }) as Promise<NoticeBoardResponse>
          );
        }

        const remainingNoticeList = await Promise.all(fetchAllPromise);

        if (!isMounted) {
          return;
        }

        const formattedNoticeList = remainingNoticeList.reduce<NoticeItem[]>((accumulator, response) => {
          accumulator.push(...response.data);

          return accumulator;
        }, []);

        setNoticeList(formattedNoticeList);
      } catch (error) {
        if (isMounted) {
          handleError(error);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void loadNoticeBoard();

    return () => {
      isMounted = false;
    };
  }, [setQuery, typedQuery.q, typedQuery.year]);

  const currentNoticeList = useMemo(
    () => noticeList.filter((notice) => currentDate >= notice.startDate && currentDate <= notice.endDate),
    [currentDate, noticeList]
  );

  const pastNoticeList = useMemo(
    () => noticeList.filter((notice) => !(currentDate >= notice.startDate && currentDate <= notice.endDate)),
    [currentDate, noticeList]
  );

  const sortedNoticeList = useMemo(
    () => [...currentNoticeList, ...pastNoticeList],
    [currentNoticeList, pastNoticeList]
  );

  const yearSelectValue: YearOption = YEAR_OPTIONS.find((opt) => opt.value === Number(filters.year)) ?? YEAR_OPTIONS[1];

  return (
    <main>
      <div className="container">
        <div className={classNames('full-scope-card', 'mbpx-0')}>
          <div className="full-scope-card__header table-header name-wrap">
            <div className="d-flex">
              <h3 className="table-title">Noticeboard</h3>
            </div>
            <div className="d-flex align-items-center notice-board-filter custom-scroll-bar">
              <div className="search-wrapper notice-search d-flex align-items-center">
                <MdSearch className="search-icon" />
                <input
                  className="search-bar notice-search__input"
                  value={searchQuery}
                  type="text"
                  placeholder="Search notice"
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      handleApplyFilter();
                    }
                  }}
                  onChange={handleSearchChange}
                />
              </div>
              <div className="hpx-45 mlpx-5">
                <YearSelect
                  isSearchable={true}
                  options={YEAR_OPTIONS}
                  autoResize={true}
                  onChange={handleYearChange}
                  value={yearSelectValue}
                  placeholder="Year"
                />
              </div>
              <div className="title-bar__filter-buttons d-flex align-items-center">
                <button
                  className={classNames('button m-10 ml-24 wpx-96', {
                    'button--primary': !isFilterChanged,
                    'button--secondary': isFilterChanged,
                  })}
                  onClick={handleApplyFilter}
                >
                  Apply Filter
                </button>

                <button
                  className={classNames('button__reset', {
                    'button__reset--active': isFilterApplied,
                  })}
                  onClick={handleReset}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        <div
          className={classNames('mtpx-24 mbpx-10 notice-board-container', {
            'notice-board-container--empty': !noticeCount && !isLoading,
          })}
        >
          {isLoading ? (
            <SkeletonTheme baseColor="#e9edf0" highlightColor="#F0F3F5">
              <Skeleton width={152} height={16} />
              <div className="full-scope-card__content mtpx-16">
                <div>
                  {Array.from({ length: 7 }).map((_, index) => (
                    <Skeleton className="notice-board-card-loader" key={index} />
                  ))}
                </div>
              </div>
            </SkeletonTheme>
          ) : noticeCount ? (
            <>
              <span className="notice-board__count">{`Showing ${noticeCount} notices of ${typedQuery.year}`}</span>
              <div className="full-scope-card__content mtpx-16">
                {sortedNoticeList.map((noticeItem) => (
                  <NoticeBoardCard notice={noticeItem} key={noticeItem.id} />
                ))}
              </div>
            </>
          ) : (
            <Empty message="Notices not found." />
          )}
        </div>
      </div>
    </main>
  );
};

export default NoticeBoard;
